import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { AncButtonDirective } from '../anc-button/anc-button.directive';
import { AncHeaderAction, AncHeaderActionsCollapseBelow } from './anc-header-actions.model';
import { ancDropdownPopperOptions } from '../../utils/anc-dropdown-popper.util';
import { AncViewportService, isAncViewportBelow } from '../../services/anc-viewport.service';

/** Max inline actions before collapsing into a more-actions dropdown. */
const INLINE_ACTION_THRESHOLD = 2;

/**
 * Header actions: renders ≤2 actions as tertiary buttons,
 * otherwise collapses all actions into a single more-actions dropdown.
 * With `collapseBelow` set (OEM-7023, FR-021), actions also collapse into the
 * dropdown on viewports narrower than that class, so they never wrap unpredictably.
 */
@Component({
    selector: 'lib-anc-header-actions',
    standalone: true,
    imports: [CommonModule, TranslateModule, NgbDropdownModule, AncButtonDirective],
    templateUrl: './anc-header-actions.component.html',
    styleUrls: ['./anc-header-actions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncHeaderActionsComponent {
    /** Widens ng-bootstrap's 2px trigger offset to the DS 8px. */
    readonly popperOptions = ancDropdownPopperOptions;

    /** Already-resolved, already-ordered list of header actions. Empty => nothing renders. */
    actions = input<AncHeaderAction[]>([]);

    /**
     * Collapse into the dropdown below this viewport class: `'expanded'` = below 840px,
     * `'laptop'` = below 1200px. Default `null` keeps the width-independent behaviour.
     */
    collapseBelow = input<AncHeaderActionsCollapseBelow | null>(null);

    /** Emits the clicked action's id. */
    actionClick = output<string>();

    private readonly viewport = inject(AncViewportService);

    /** More than 2 actions: all go in the dropdown at every width (unchanged behaviour). */
    private readonly overThreshold = computed(() => this.actions().length > INLINE_ACTION_THRESHOLD);

    /** Narrow viewport with `collapseBelow` set: secondary actions fold into the dropdown. */
    private readonly collapsedByViewport = computed(() => {
        const collapseBelow = this.collapseBelow();
        return !!collapseBelow && isAncViewportBelow(this.viewport.viewportClass(), collapseBelow);
    });

    /** Actions rendered as buttons. */
    readonly inlineActions = computed(() => {
        if (this.overThreshold()) return [];
        const actions = this.actions();
        return this.collapsedByViewport() ? actions.filter((action) => action.primary) : actions;
    });

    /** Actions rendered in the more-actions dropdown. */
    readonly menuActions = computed(() => {
        const actions = this.actions();
        if (this.overThreshold()) return actions;
        return this.collapsedByViewport() ? actions.filter((action) => !action.primary) : [];
    });

    readonly showAsDropdown = computed(() => this.menuActions().length > 0);

    onItem(action: AncHeaderAction): void {
        if (action.disabled) {
            return;
        }
        this.actionClick.emit(action.id);
    }

    inlineLabel(action: AncHeaderAction): string {
        return action.buttonLabel ?? action.label;
    }
}
